import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Button } from "@components/ui/button";
import { SparklesIcon, CheckIcon } from "@heroicons/react/24/outline";
import { CancelSubscriptionModal } from "./CancelSubscriptionModal";
import { ConfirmUpgradeModal } from "./ConfirmUpgradeModal";
import { PlanSelector } from "./PlanSelector";
import { useReactivateSubscription } from "./hooks/useReactivateSubscription";
import type { SettingsSubscription, SettingsAvailablePlan } from "@pages/Settings";

interface SubscriptionSectionProps {
  subscription: SettingsSubscription | null;
  stripePortalUrl?: string;
  availablePlans: SettingsAvailablePlan[];
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function SubscriptionSection({ subscription, availablePlans }: SubscriptionSectionProps) {
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [planSelectorOpen, setPlanSelectorOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const { reactivate, isReactivating } = useReactivateSubscription({
    subscriptionPrefixId: subscription?.prefix_id,
  });

  const isCancelled = subscription?.ends_at != null;
  const isTrialing = subscription?.is_trialing === true;
  const trialEndsAt = subscription?.trial_ends_at ?? null;

  const trialDaysRemaining = (() => {
    if (!trialEndsAt) return null;
    const ms = new Date(trialEndsAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  })();

  if (!subscription) {
    return (
      <>
        <Card className="bg-white border-neutral-300 rounded-2xl w-full lg:w-[911px]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-green-100">
                <SparklesIcon className="h-4 w-4 text-accent-green-500" />
              </div>
              <CardTitle className="font-['Plus_Jakarta_Sans'] text-lg font-semibold text-[#2E3238]">
                Subscription
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="font-['Plus_Jakarta_Sans'] text-sm text-[#74767A]">
              No active subscription.
            </p>
            <Button
              onClick={() => setPlanSelectorOpen(true)}
              className="mt-4 bg-[#3748B8] hover:bg-[#2d3a9a] text-white font-['Plus_Jakarta_Sans']"
            >
              View Plans
            </Button>
          </CardContent>
        </Card>

        <PlanSelector
          open={planSelectorOpen}
          onOpenChange={setPlanSelectorOpen}
          plans={availablePlans}
          currentTierName={null}
          currentInterval={null}
          subscriptionPrefixId={null}
          isCancelled={false}
          hasSubscription={false}
          processor={null}
        />
      </>
    );
  }

  return (
    <>
      <Card className="bg-white border-neutral-300 rounded-2xl w-full lg:w-[911px]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-green-100">
                <SparklesIcon className="h-4 w-4 text-accent-green-500" />
              </div>
              <CardTitle className="font-['Plus_Jakarta_Sans'] text-lg font-semibold text-[#2E3238]">
                Subscription
              </CardTitle>
            </div>
            {isCancelled ? (
              <div className="flex items-center gap-4">
                <Button
                  onClick={reactivate}
                  disabled={isReactivating}
                  className="bg-[#2E3238] hover:bg-[#1a1e22] text-white font-['Plus_Jakarta_Sans'] text-sm py-1.5 px-4 h-auto disabled:opacity-50"
                >
                  {isReactivating ? "Reactivating..." : "Reactivate Plan"}
                </Button>
                <Button
                  onClick={() => setPlanSelectorOpen(true)}
                  variant="outline"
                  className="font-['Plus_Jakarta_Sans'] text-sm py-1.5 px-4 h-auto"
                >
                  Change Plan
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => setCancelModalOpen(true)}
                  className="font-['Plus_Jakarta_Sans'] text-sm text-[#D14F34] hover:text-[#D14F34] hover:bg-transparent"
                >
                  {isTrialing ? "Cancel Trial" : "Cancel Subscription"}
                </Button>
                <Button
                  onClick={() =>
                    isTrialing ? setUpgradeModalOpen(true) : setPlanSelectorOpen(true)
                  }
                  className="bg-[#2E3238] hover:bg-[#1a1e22] text-white font-['Plus_Jakarta_Sans'] text-sm py-1.5 px-4 h-auto"
                >
                  {isTrialing ? "Upgrade Now" : "Change Plan"}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5 w-full lg:max-w-[704px]">
          {/* Plan Info */}
          <div className="space-y-1">
            <p className="font-['Plus_Jakarta_Sans'] text-xs text-[#96989B]">
              {isTrialing ? "Free Trial" : "Current Plan"}
            </p>
            <div className="flex items-center gap-2">
              <p className="font-['Plus_Jakarta_Sans'] text-base font-medium text-[#0F1113]">
                {subscription.plan_display_name}
              </p>
              {isTrialing && (
                <span
                  data-testid="subscription-trial-badge"
                  className="px-2 py-0.5 rounded-full bg-accent-green-100 text-accent-green-700 font-['Plus_Jakarta_Sans'] text-xs font-medium"
                >
                  Trial
                </span>
              )}
              {isCancelled && (
                <span className="px-2 py-0.5 rounded-full bg-[#FEF3E7] text-[#D97706] font-['Plus_Jakarta_Sans'] text-xs font-medium">
                  Cancelled
                </span>
              )}
            </div>
            {isTrialing && trialEndsAt && (
              <p
                data-testid="subscription-trial-ends"
                className="font-['Plus_Jakarta_Sans'] text-sm text-[#74767A] pt-1"
              >
                {trialDaysRemaining !== null && trialDaysRemaining > 0
                  ? `${trialDaysRemaining} ${trialDaysRemaining === 1 ? "day" : "days"} left · Trial ends ${formatDate(trialEndsAt)} — you haven’t been charged yet.`
                  : `Trial ends ${formatDate(trialEndsAt)} — you haven’t been charged yet.`}
              </p>
            )}
          </div>

          {/* Features */}
          {subscription.features.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <p className="font-['Plus_Jakarta_Sans'] text-sm font-medium text-[#0F1113]">
                  What&apos;s included
                </p>
                {isCancelled && subscription.ends_at && (
                  <>
                    <span className="text-[#96989B]">•</span>
                    <p className="font-['Plus_Jakarta_Sans'] text-sm text-[#96989B]">
                      Expires {formatDate(subscription.ends_at)}
                    </p>
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                {subscription.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckIcon className="h-4 w-4 text-[#2E9E72] flex-shrink-0" />
                    <span className="font-['Plus_Jakarta_Sans'] text-sm text-[#74767A]">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CancelSubscriptionModal
        open={cancelModalOpen}
        onOpenChange={setCancelModalOpen}
        subscriptionPrefixId={subscription.prefix_id}
        currentPeriodEnd={subscription.current_period_end}
      />

      <PlanSelector
        open={planSelectorOpen}
        onOpenChange={setPlanSelectorOpen}
        plans={availablePlans}
        currentTierName={subscription.plan_name?.split("_")[0] ?? null}
        currentInterval={subscription.interval}
        subscriptionPrefixId={subscription.prefix_id}
        isCancelled={isCancelled}
        hasSubscription={true}
        processor={subscription.processor ?? null}
      />

      {isTrialing && subscription.amount_cents != null && (
        <ConfirmUpgradeModal
          open={upgradeModalOpen}
          onOpenChange={setUpgradeModalOpen}
          subscriptionPrefixId={subscription.prefix_id}
          planDisplayName={subscription.plan_display_name}
          amountCents={subscription.amount_cents}
          currency={subscription.currency ?? "usd"}
          interval={subscription.interval}
        />
      )}
    </>
  );
}
